import { useState } from 'react';
import { z } from 'zod';
import { Loader2, Pencil, Plus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FlagWriteConfirmDialog } from '@/components/feature-flags/flag-write-confirm-dialog';
import { requiresConfirmation } from '@/lib/api-env';
import { env } from '@/config/env';
import {
  useCreateFlagRule,
  useDeleteFlagRule,
  useFlagRules,
  useUpdateFlagRule,
} from '@/hooks/use-feature-flags';
import {
  FLAG_CONFIRM_ON_ENABLE,
  RULE_KINDS,
  RULE_KIND_COPY,
  RULE_KIND_RANK,
  acceptsTargeting,
  describeRule,
  getEnforcedFlagDisableCopy,
  isRuleKind,
  ruleKindOf,
  splitRuleEntries,
} from '@/types/feature-flag';
import type {
  FeatureFlagDefinition,
  FeatureFlagRule,
  FlagRuleBody,
  FlagScope,
  RuleKind,
} from '@/types/feature-flag';

interface FlagRulesPanelProps {
  scope: FlagScope;
  definition: FeatureFlagDefinition | null;
  onClose: () => void;
}

interface DraftRule {
  /** null while adding — the row has no server id yet. */
  id: string | null;
  kind: RuleKind;
  value: string;
  enabled: boolean;
  isActive: boolean;
  note: string;
}

const NEW_RULE: DraftRule = {
  id: null,
  kind: 'exactEmail',
  value: '',
  enabled: true,
  isActive: true,
  note: '',
};

function toDraft(rule: FeatureFlagRule): DraftRule {
  const kind = ruleKindOf(rule);
  const entries = kind === 'subject' ? (rule.userIds ?? []) : (rule.emails ?? []);
  return {
    id: rule.id,
    kind,
    value: entries.join('\n'),
    enabled: rule.enabled,
    isActive: rule.isActive,
    note: rule.note ?? '',
  };
}

/** One axis per rule: the kind decides which array carries the entries, and
 * the other is explicitly nulled so the backend's one-axis check passes on an
 * edit that switches kind. */
function toBody(draft: DraftRule): FlagRuleBody {
  const entries = splitRuleEntries(draft.value);
  const isSubject = draft.kind === 'subject';
  return {
    enabled: draft.enabled,
    userIds: isSubject ? entries : null,
    emails: isSubject ? null : entries,
    emailMatch: draft.kind === 'containsEmail' ? 'contains' : 'exact',
    isActive: draft.isActive,
    note: draft.note.trim() === '' ? null : draft.note.trim(),
  };
}

// Mirrors the backend's per-entry rules verbatim (ruleShape in
// FeatureFlagAdmin.controller.ts) so a rejection is an inline message rather
// than a round trip that comes back 400. Same zod email check, same bounds.
const MAX_ENTRIES = 200;
const SUBJECT_ENTRY_MAX = 128;
const EMAIL_ENTRY_MAX = 320;
const NOTE_MAX = 500;
const exactEmailSchema = z.string().email();

function validateDraft(draft: DraftRule): string | null {
  const entries = splitRuleEntries(draft.value);
  const isSubject = draft.kind === 'subject';

  if (entries.length === 0) {
    return `Add at least one ${isSubject ? 'user ID' : 'entry'}.`;
  }
  if (entries.length > MAX_ENTRIES) {
    return `A rule can name at most ${MAX_ENTRIES} entries — this one has ${entries.length}.`;
  }

  const tooLong = entries.find(
    entry => entry.length > (isSubject ? SUBJECT_ENTRY_MAX : EMAIL_ENTRY_MAX)
  );
  if (tooLong !== undefined) {
    return `"${tooLong.slice(0, 24)}…" is longer than ${isSubject ? SUBJECT_ENTRY_MAX : EMAIL_ENTRY_MAX} characters.`;
  }

  if (draft.kind === 'exactEmail') {
    const bad = entries.find(entry => !exactEmailSchema.safeParse(entry).success);
    if (bad !== undefined) {
      return `"${bad}" is not a full email address — switch to "Any email containing a fragment" to match part of one.`;
    }
  }
  if (draft.kind === 'containsEmail') {
    const short = entries.find(entry => entry.length < 3);
    if (short !== undefined) {
      return `"${short}" is too short — use at least 3 characters, e.g. @moxielabs.co.`;
    }
  }
  return null;
}

export function FlagRulesPanel({ scope, definition, onClose }: FlagRulesPanelProps) {
  const [draft, setDraft] = useState<DraftRule | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<DraftRule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FeatureFlagRule | null>(null);
  const prodGuard = requiresConfirmation(env.VITE_API_URL);

  const targetable = definition ? acceptsTargeting(definition) : false;
  // Enforced flags refuse platform overrides, so their fallback is the stored value alone.
  const fallback = definition?.enforced
    ? 'the stored value'
    : 'the platform overrides, then the stored value';
  const flagKey = definition?.key ?? '';
  const confirmCopy = FLAG_CONFIRM_ON_ENABLE[flagKey];
  // A flag enforced from its stored value alone never evaluates a rule, so the API
  // would reject any write here — don't even fetch.
  const { data, isLoading } = useFlagRules(scope, flagKey, definition !== null && targetable);

  const createMutation = useCreateFlagRule();
  const updateMutation = useUpdateFlagRule();
  const deleteMutation = useDeleteFlagRule();
  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const rules = data?.rules ?? [];
  const pendingDeleteId = deleteMutation.isPending ? deleteMutation.variables?.ruleId : undefined;

  // A rejected write would otherwise fail silently — the form just stays open
  // with no reason given. The server's message is the useful one (it names the
  // non-targetable flag, the archived flag, or the offending entry).
  const writeError = createMutation.error ?? updateMutation.error ?? null;
  const deleteError = deleteMutation.error ?? null;

  function closeForm() {
    setDraft(null);
    setFormError(null);
    createMutation.reset();
    updateMutation.reset();
  }

  /** The panel stays mounted when `definition` goes null, so anything left in
   * state survives into the next flag it is opened for — a stale draft whose
   * id is absent from the new list renders no form while still hiding the
   * Add button. Every close path goes through here. */
  function closePanel() {
    closeForm();
    setPendingConfirm(null);
    setPendingDelete(null);
    deleteMutation.reset();
    onClose();
  }

  function persist(target: DraftRule) {
    const body = toBody(target);
    const onSettled = { onSuccess: () => closeForm() };
    if (target.id === null) {
      createMutation.mutate({ scope, key: flagKey, body }, onSettled);
    } else {
      updateMutation.mutate({ scope, key: flagKey, ruleId: target.id, body }, onSettled);
    }
  }

  function submitDraft() {
    if (!draft) return;
    const problem = validateDraft(draft);
    if (problem !== null) {
      setFormError(problem);
      return;
    }
    // Turning a takeover-class flag ON for one person is still turning it on,
    // so it gets the same confirmation the page's toggle uses — and on the
    // prod dashboard every rule write pauses, as every toggle there does.
    if ((draft.enabled && confirmCopy !== undefined) || prodGuard) {
      setPendingConfirm(draft);
      return;
    }
    persist(draft);
  }

  /** Where this draft will sit once saved. Rules are ordered by kind, so a
   * kind change moves the row — showing the position keeps the fixed order
   * from feeling arbitrary. */
  function draftPosition(): number {
    const rank = RULE_KIND_RANK[draft?.kind ?? 'subject'];
    const ahead = rules.filter(
      rule => rule.id !== draft?.id && RULE_KIND_RANK[ruleKindOf(rule)] <= rank
    ).length;
    return ahead + 1;
  }

  return (
    <Dialog
      open={definition !== null}
      onOpenChange={next => {
        // Save is disabled while a write is in flight; Escape and the overlay
        // must not be a way around that.
        if (next || busy) return;
        closePanel();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Identity targeting for {definition?.label}</DialogTitle>
          {targetable && (
            <DialogDescription>
              A rule decides the flag outright for the people it names — it beats{' '}
              {definition?.enforced ? 'the stored value' : 'platform overrides and the stored value'}
              , and it can turn the flag on as well as off.
              {flagKey === 'groupOrdering' &&
                ' Guests follow the host: if the person who started a group order has it on, the guests they invite can join.'}
            </DialogDescription>
          )}
        </DialogHeader>

        {!targetable ? (
          <Alert>
            <AlertDescription>
              <strong>{definition?.label}</strong> is enforced by the backend — it{' '}
              {getEnforcedFlagDisableCopy(flagKey)} It reads only the stored value, so identity
              targeting does not apply to it.
            </AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Evaluated top to bottom, first match wins. Position is set by rule type — you
              can&apos;t reorder it.
            </p>

            {deleteError !== null && (
              <Alert variant="destructive">
                <AlertDescription>{deleteError.message}</AlertDescription>
              </Alert>
            )}

            {rules.length === 0 && draft === null && (
              <div className="rounded-md border border-dashed p-5 text-center">
                <p className="text-sm font-medium">No one is targeted yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Everyone resolves from {fallback}.
                </p>
              </div>
            )}

            {rules.map((rule, index) => {
              const isEditing = draft?.id === rule.id;
              const kind = ruleKindOf(rule);
              return (
                <div key={rule.id} className="rounded-md border p-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
                      {index + 1}
                    </span>
                    <div className="min-w-0 grow space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-sm font-medium ${rule.isActive ? '' : 'text-muted-foreground'}`}
                        >
                          {describeRule(rule)}
                        </span>
                        <Badge variant="outline">{RULE_KIND_COPY[kind].tier}</Badge>
                      </div>
                      {kind === 'subject' && !isEditing && (
                        <p className="break-all font-mono text-xs text-muted-foreground">
                          {(rule.userIds ?? []).join(', ')}
                        </p>
                      )}
                      {rule.note !== null && rule.note !== '' && !isEditing && (
                        <p className="text-xs text-muted-foreground">{rule.note}</p>
                      )}
                      {!rule.isActive && (
                        <p className="text-xs text-muted-foreground">
                          Paused — kept in the list, skipped when resolving.
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Badge variant={rule.enabled ? 'success' : 'secondary'}>
                        {rule.enabled ? 'ON' : 'OFF'}
                      </Badge>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label={`Edit the rule for ${describeRule(rule)}`}
                        disabled={busy}
                        onClick={() => {
                          setFormError(null);
                          setDraft(toDraft(rule));
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        aria-label={`Delete the rule for ${describeRule(rule)}`}
                        disabled={busy}
                        onClick={() => {
                          if (prodGuard) {
                            setPendingDelete(rule);
                            return;
                          }
                          deleteMutation.mutate({ scope, key: flagKey, ruleId: rule.id });
                        }}
                      >
                        {pendingDeleteId === rule.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {isEditing && draft !== null && (
                    <RuleDraftForm
                      draft={draft}
                      position={draftPosition()}
                      flagLabel={definition?.label ?? ''}
                      error={formError ?? writeError?.message ?? null}
                      busy={busy}
                      onChange={setDraft}
                      onCancel={closeForm}
                      onSubmit={submitDraft}
                    />
                  )}
                </div>
              );
            })}

            {draft !== null && draft.id === null && (
              <div className="rounded-md border border-primary p-3">
                <p className="text-sm font-medium">New rule</p>
                <RuleDraftForm
                  draft={draft}
                  position={draftPosition()}
                  flagLabel={definition?.label ?? ''}
                  error={formError ?? writeError?.message ?? null}
                  busy={busy}
                  onChange={setDraft}
                  onCancel={closeForm}
                  onSubmit={submitDraft}
                />
              </div>
            )}

            {draft === null && (
              <Button
                variant="outline"
                className="w-full border-dashed"
                disabled={busy}
                onClick={() => {
                  setFormError(null);
                  setDraft(NEW_RULE);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add rule
              </Button>
            )}

            <div className="flex items-start gap-3 rounded-md border bg-muted/40 p-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-background text-[11px] font-semibold text-muted-foreground">
                —
              </span>
              <div className="grow">
                <p className="text-sm font-medium">Everyone else</p>
                <p className="text-xs text-muted-foreground">
                  Falls through to {fallback}.
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={closePanel}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      <FlagWriteConfirmDialog
        open={pendingConfirm !== null}
        title={
          pendingConfirm?.enabled === true && confirmCopy !== undefined
            ? confirmCopy.title
            : 'Save this targeting rule?'
        }
        description={
          pendingConfirm?.enabled === true && confirmCopy !== undefined
            ? `${confirmCopy.description} This rule turns it on only for the people it names, but for them the effect is the same as flipping the flag.`
            : `${definition?.label ?? 'This flag'} will resolve to ${pendingConfirm?.enabled === true ? 'ON' : 'OFF'} for everyone this rule names, ahead of ${definition?.enforced ? 'its stored value' : 'any platform override'}.`
        }
        prodGuard={prodGuard}
        busy={busy}
        onConfirm={() => {
          if (pendingConfirm !== null) persist(pendingConfirm);
          setPendingConfirm(null);
        }}
        onCancel={() => setPendingConfirm(null)}
      />

      <FlagWriteConfirmDialog
        open={pendingDelete !== null}
        title="Remove this targeting rule?"
        description={`The people it names fall back to ${fallback} of ${definition?.label ?? 'the flag'}.`}
        prodGuard={prodGuard}
        busy={busy}
        onConfirm={() => {
          if (pendingDelete !== null) {
            deleteMutation.mutate({ scope, key: flagKey, ruleId: pendingDelete.id });
          }
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />

    </Dialog>
  );
}

interface RuleDraftFormProps {
  draft: DraftRule;
  position: number;
  flagLabel: string;
  error: string | null;
  busy: boolean;
  onChange: (draft: DraftRule) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function RuleDraftForm({
  draft,
  position,
  flagLabel,
  error,
  busy,
  onChange,
  onCancel,
  onSubmit,
}: RuleDraftFormProps) {
  const copy = RULE_KIND_COPY[draft.kind];

  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      <div className="space-y-1.5">
        <Label htmlFor="rule-kind">Who this rule names</Label>
        <Select
          value={draft.kind}
          onValueChange={next => {
            // The kind is the rule's position in the chain, so changing it
            // moves the row — that is the whole point of the select.
            if (isRuleKind(next)) onChange({ ...draft, kind: next });
          }}
        >
          <SelectTrigger id="rule-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RULE_KINDS.map(kind => (
              <SelectItem key={kind} value={kind}>
                {RULE_KIND_COPY[kind].option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{copy.help}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rule-entries">{copy.valueLabel}</Label>
        <Textarea
          id="rule-entries"
          rows={2}
          className="font-mono text-xs"
          placeholder={copy.placeholder}
          value={draft.value}
          onChange={event => onChange({ ...draft, value: event.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          One per line. Guests are never matched — a rule only applies to someone signed in.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Result when it matches</p>
          <p className="text-xs text-muted-foreground">
            {flagLabel} becomes {draft.enabled ? 'available' : 'hidden'} for these people.
          </p>
        </div>
        <Switch
          checked={draft.enabled}
          aria-label="Result when this rule matches"
          onCheckedChange={next => onChange({ ...draft, enabled: next })}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Active</p>
          <p className="text-xs text-muted-foreground">
            A paused rule keeps its place but is skipped when resolving.
          </p>
        </div>
        <Switch
          checked={draft.isActive}
          aria-label="Whether this rule is active"
          onCheckedChange={next => onChange({ ...draft, isActive: next })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rule-note">Note (optional)</Label>
        <Input
          id="rule-note"
          maxLength={NOTE_MAX}
          placeholder="Why this rule exists"
          value={draft.note}
          onChange={event => onChange({ ...draft, note: event.target.value })}
        />
      </div>

      <p className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        Lands at position <strong className="text-foreground">{position}</strong> — {copy.reason}
      </p>

      {error !== null && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={busy} onClick={onSubmit}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save rule
        </Button>
      </div>
    </div>
  );
}
